import { useState } from 'react'
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import { ExpensableGroupsDataCollectionDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { mapFiltersToVariables } from 'modules/expenses/components/expenses/DataCollection/filters/filterUtils'
import { useDataCollectionConfig } from 'modules/expenses/components/expenses/DataCollection/useDataCollectionConfig'
import { useDataCollectionFilterExtraction } from 'modules/expenses/components/expenses/DataCollection/useDataCollectionFilterExtraction'
import { useExpensesListPage } from 'modules/expenses/hooks/useExpensesListPage'
import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'
import { usePageDateFilters } from 'modules/expenses/lib/PageDateFiltersContext'
import { type RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { isMyExpensesSection } from 'modules/expenses/lib/common'
import { usePageActions } from 'modules/expenses/pages/my_expenses/usePageActions'

import { useExpensableGroupsStatusCounts } from './useExpensableGroupsStatusCounts'
import { useGroupsDataCollection } from './useGroupsDataCollection'
import { useGroupsFilters } from './useGroupsFilters'

interface Props {
  employeeIds?: number[]
}

export const GroupsDataCollection = ({ employeeIds }: Props) => {
  const context = useOutletContext<RouteContextParams>()
  const { section: expensesContext, expensableGroupsEnabled } = context
  const { navigation } = useExpensesNavigation()
  const navigate = useNavigate()
  const {
    company,
    access: { locale },
  } = useCurrentSession()

  const isMyExpenses = isMyExpensesSection(expensesContext)
  const { sortings, columns } = useGroupsDataCollection({ isMyExpenses, locale })
  const { dateFilters, setPageDateFilters } = usePageDateFilters()
  const filterDefinitions = useGroupsFilters({ companyId: company.id, isMyExpenses })

  const [currentFilters, setCurrentFilters] = useState<Record<string, unknown> | undefined>(
    undefined
  )
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined)

  const { employeeIds: filterEmployeeIds, filter: additionalFilters } =
    useDataCollectionFilterExtraction(currentFilters, { defaultEmployeeIds: employeeIds })

  const statusCounts = useExpensableGroupsStatusCounts({
    companyId: company.id,
    employeeIds: filterEmployeeIds,
    from: dateFilters.startDate,
    to: dateFilters.endDate,
    filter: additionalFilters,
    search: searchTerm,
  })

  const { navigationFilters: navigationFiltersDefinitions, presets: presetsDefinitions } =
    useDataCollectionConfig({ includeDraft: isMyExpenses, statusCounts })

  const { hasExpensesPerDiemsFeature: canUsePerDiems, canCreateExpenses } = useExpensesListPage({
    companyId: company.id,
    skipProjectsQuery: true,
  })

  const { actions } = usePageActions({
    isInMyExpensesSection: isMyExpenses,
    canUsePerDiems,
    canCreateExpenses,
    expensableGroupsEnabled,
  })

  const back = { href: navigation.list.fullPath() }

  const dataSource = useGraphqlDataCollectionSource({
    document: ExpensableGroupsDataCollectionDocument,
    mapData: (data) => data.expenses.expensableGroupsConnection.nodes,
    mapVariables: ({ navigationFilters, sortings: mapSortings, filters, search }) => {
      const { employeeIds: ownerFilterIds, filter } = mapFiltersToVariables(filters)

      // Use filter employeeIds if set (user selected owner filter), otherwise fall back to prop employeeIds (page context)
      const effectiveEmployeeIds = ownerFilterIds?.length ? ownerFilterIds : employeeIds

      const variables = {
        companyId: company.id,
        employeeIds: effectiveEmployeeIds,
        sortOrder: mapSortings?.[0],
        includeDrafts: isMyExpenses,
        filter,
        from: navigationFilters?.date?.value?.from,
        to: navigationFilters?.date?.value?.to,
        search,
      }
      return variables
    },
    itemUrl: (item) => navigation.groups.id.fullPath({ groupId: item.id }),
    paginationType: 'infinite-scroll',
    mapPagination: (data) => ({
      total: data.expenses.expensableGroupsConnection.totalCount ?? 0,
      perPage: 20,
      cursor: data.expenses.expensableGroupsConnection.pageInfo.endCursor ?? undefined,
      hasNextPage: data.expenses.expensableGroupsConnection.pageInfo.hasNextPage ?? false,
    }),
    navigationFilters: navigationFiltersDefinitions,
    filters: filterDefinitions,
    presets: presetsDefinitions,
    sortings,
    search: {
      enabled: true,
    },
    primaryActions: () => {
      if (!actions.primary || actions.primary.length === 0) {
        return undefined
      }

      // Only show "Add Group" action in Groups tab
      const groupAction = actions.primary.find((action) => action.key === 'new_group')

      if (!groupAction) {
        return undefined
      }

      return [
        {
          label: i18n.t('expenses_v2.buttons.add_group'),
          icon: Plus,
          onClick: () => navigate(groupAction.to),
        },
      ]
    },
  })

  return (
    <>
      <Outlet context={{ ...context, back }} />
      <Box paddingX="s12" style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
        <OneDataCollection
          id={isMyExpenses ? 'my-groups/data-collection/v2' : 'groups/data-collection/v2'}
          storage={{
            features: ['filters', 'sortings', 'search'],
          }}
          fullHeight
          source={dataSource}
          visualizations={[
            {
              type: 'table',
              options: {
                allowColumnHiding: true,
                allowColumnReordering: true,
                columns,
              },
            },
          ]}
          onStateChange={(state) => {
            setCurrentFilters(state.filters)
            setSearchTerm(state.search)

            const { from, to } = state.navigationFilters?.date?.value || {}
            if (from && to) {
              if (
                dateFilters.startDate.getTime() !== from.getTime() ||
                dateFilters.endDate.getTime() !== to.getTime()
              ) {
                setPageDateFilters(from, to)
              }
            }
          }}
        />
      </Box>
    </>
  )
}
